import { Loader } from "@circle-react-shared/uikit/Loader";
import { GOOGLE_ONE_TAP_REDIRECTING_EVENT } from "@circle-react/constants/googleOneTapEvents";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  communitySignInUrl,
  communitySignupUrl,
  shouldShowSignupLink,
} from "@circle-react/helpers/communityHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { Button as CompassButton } from "@circleco/compass/components/Button";
import { useState, useEffect } from "react";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useSitePageMetadata } from "@/react/routes/Main/useSitePageMetadata";

export interface ConditionallyRenderLoginSignupButtonsProps {
  hideSignup?: boolean;
}

export const ConditionallyRenderLoginSignupButtons = ({
  hideSignup,
}: ConditionallyRenderLoginSignupButtonsProps) => {
  const { currentCommunity, currentUser, isLoading } = usePunditUserContext();
  const [isOneTapRedirecting, setIsOneTapRedirecting] = useState(false);
  const { isCircleV4 } = useCircleV4();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleOneTapRedirect = () => {
      setIsOneTapRedirecting(true);
      timeoutId = setTimeout(() => setIsOneTapRedirecting(false), 5000);
    };
    window.addEventListener(
      GOOGLE_ONE_TAP_REDIRECTING_EVENT,
      handleOneTapRedirect,
    );
    return () => {
      window.removeEventListener(
        GOOGLE_ONE_TAP_REDIRECTING_EVENT,
        handleOneTapRedirect,
      );
      clearTimeout(timeoutId);
    };
  }, []);
  const { isSitePageRoute } = useSitePageMetadata();
  const shouldSkipRedirect = isSitePageRoute;
  const {
    post_login_redirect: postLoginRedirectUrlParam,
  }: { post_login_redirect?: string } = useRouterQueryParams();

  if (isLoading) {
    return <Loader className="flex !py-0" />;
  }

  if (currentUser) {
    return null;
  }

  const hasPrivateSignUpUrl = !!currentCommunity?.private_sign_up_url;

  const hasSignUpLink =
    shouldShowSignupLink(currentCommunity) || hasPrivateSignUpUrl;

  const signupUrl = hasPrivateSignUpUrl
    ? currentCommunity?.private_sign_up_url
    : communitySignupUrl(
        currentCommunity,
        postLoginRedirectUrlParam,
        shouldSkipRedirect,
      );

  const signUpLabel = hasPrivateSignUpUrl
    ? currentCommunity?.private_sign_up_label
    : t("join");

  const handleSignUpClick = () => {
    if (!signupUrl) return;
    window.location.href = signupUrl;
  };

  const handleSignInClick = () => {
    const params = shouldSkipRedirect
      ? {}
      : {
          post_login_redirect:
            postLoginRedirectUrlParam || window.location.href,
        };
    window.location.href = communitySignInUrl(currentCommunity, params);
  };

  return (
    <div
      className={classNames("hidden items-center justify-end gap-2.5 md:flex")}
    >
      {isCircleV4 ? (
        <CompassButton
          variant="outline"
          size="md"
          disabled={isOneTapRedirecting}
          onClick={handleSignInClick}
        >
          {t("log_in")}
        </CompassButton>
      ) : (
        <Button
          type="button"
          variant="secondary"
          large
          disabled={isOneTapRedirecting}
          onClick={handleSignInClick}
        >
          {t("log_in")}
        </Button>
      )}

      {!hideSignup &&
        hasSignUpLink &&
        (isCircleV4 ? (
          <CompassButton
            variant="branded"
            size="md"
            disabled={isOneTapRedirecting}
            onClick={handleSignUpClick}
          >
            {signUpLabel}
          </CompassButton>
        ) : (
          <Button
            type="button"
            variant="primary"
            large
            disabled={isOneTapRedirecting}
            onClick={handleSignUpClick}
          >
            {signUpLabel}
          </Button>
        ))}
    </div>
  );
};
